import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Navbar from '../../components/Navbar';
import Footer from '../../components/Footer';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import axios from 'axios';
import { Package, FileText, Search, Warehouse, Truck, Forklift, Users } from 'lucide-react';

const CustomerDashboard = () => {
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

  useEffect(() => {
    fetchBookings();
  }, []);

  const fetchBookings = async () => {
    try {
      const response = await axios.get(`${API_URL}/bookings`);
      setBookings(response.data || []);
    } catch (error) {
      console.error('Failed to fetch bookings:', error);
    } finally {
      setLoading(false);
    }
  };

  const activeBookings = bookings.filter(b => b.status === 'active' || b.status === 'pending').length;

  return (
    <div className="min-h-screen bg-secondary flex flex-col">
      <Navbar />
      <div className="flex-1 max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          <h1 className="font-heading text-4xl font-black text-foreground tracking-tight mb-2">
            Customer Dashboard
          </h1>
          <p className="text-muted-foreground">Find logistics services and manage your bookings</p>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
          <Card className="bg-white border border-border rounded-sm shadow-sm p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground uppercase tracking-wider">Total Bookings</p>
                <p className="text-3xl font-bold text-foreground mt-2">{bookings.length}</p>
              </div>
              <div className="h-12 w-12 bg-primary/10 rounded-sm flex items-center justify-center">
                <Package className="h-6 w-6 text-primary" strokeWidth={1.5} />
              </div>
            </div>
          </Card>

          <Card className="bg-white border border-border rounded-sm shadow-sm p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground uppercase tracking-wider">Active</p>
                <p className="text-3xl font-bold text-foreground mt-2">{activeBookings}</p>
              </div>
              <div className="h-12 w-12 bg-emerald-100 rounded-sm flex items-center justify-center">
                <FileText className="h-6 w-6 text-emerald-700" strokeWidth={1.5} />
              </div>
            </div>
          </Card>

          <Card className="bg-white border border-border rounded-sm shadow-sm p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground uppercase tracking-wider">Total Spent</p>
                <p className="text-3xl font-bold text-foreground mt-2">
                  ${bookings.reduce((sum, b) => sum + (b.total_amount || 0), 0).toFixed(0)}
                </p>
              </div>
              <div className="h-12 w-12 bg-purple-100 rounded-sm flex items-center justify-center">
                <Search className="h-6 w-6 text-purple-600" strokeWidth={1.5} />
              </div>
            </div>
          </Card>
        </div>

        {/* Search Services Section */}
        <Card className="bg-white border border-border rounded-sm shadow-sm p-6 mb-8">
          <h2 className="font-heading text-2xl font-bold text-foreground mb-2">Find Services</h2>
          <p className="text-muted-foreground mb-6">Search for logistics services you need - individually or combined</p>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <Button
              onClick={() => navigate('/customer/search/warehouses')}
              data-testid="search-warehouses-btn"
              variant="outline"
              className="h-auto py-6 flex flex-col items-center gap-3 rounded-sm hover:bg-primary/5 hover:border-primary transition-all"
            >
              <div className="h-14 w-14 bg-primary/10 rounded-sm flex items-center justify-center">
                <Warehouse className="h-7 w-7 text-primary" strokeWidth={1.5} />
              </div>
              <div className="text-center">
                <p className="font-bold text-foreground">Warehouses</p>
                <p className="text-xs text-muted-foreground mt-1">Storage & fulfillment</p>
              </div>
            </Button>

            <Button
              onClick={() => navigate('/customer/search/mhe')}
              data-testid="search-mhe-btn"
              variant="outline"
              className="h-auto py-6 flex flex-col items-center gap-3 rounded-sm hover:bg-amber-50 hover:border-amber-400 transition-all"
            >
              <div className="h-14 w-14 bg-amber-100 rounded-sm flex items-center justify-center">
                <Forklift className="h-7 w-7 text-amber-600" strokeWidth={1.5} />
              </div>
              <div className="text-center">
                <p className="font-bold text-foreground">MHE</p>
                <p className="text-xs text-muted-foreground mt-1">Material handling equipment</p>
              </div>
            </Button>

            <Button
              onClick={() => navigate('/customer/search/fleet')}
              data-testid="search-fleet-btn"
              variant="outline"
              className="h-auto py-6 flex flex-col items-center gap-3 rounded-sm hover:bg-blue-50 hover:border-blue-400 transition-all"
            >
              <div className="h-14 w-14 bg-blue-100 rounded-sm flex items-center justify-center">
                <Truck className="h-7 w-7 text-blue-600" strokeWidth={1.5} />
              </div>
              <div className="text-center">
                <p className="font-bold text-foreground">Fleet</p>
                <p className="text-xs text-muted-foreground mt-1">Vehicles & transport</p>
              </div>
            </Button>

            <Button
              onClick={() => navigate('/customer/search/manpower')}
              data-testid="search-manpower-btn"
              variant="outline"
              className="h-auto py-6 flex flex-col items-center gap-3 rounded-sm hover:bg-emerald-50 hover:border-emerald-400 transition-all"
            >
              <div className="h-14 w-14 bg-emerald-100 rounded-sm flex items-center justify-center">
                <Users className="h-7 w-7 text-emerald-600" strokeWidth={1.5} />
              </div>
              <div className="text-center">
                <p className="font-bold text-foreground">Manpower</p>
                <p className="text-xs text-muted-foreground mt-1">Labor & staffing</p>
              </div>
            </Button>
          </div>
        </Card>

        {/* Recent Bookings */}
        <Card className="bg-white border border-border rounded-sm shadow-sm p-6">
          <h2 className="font-heading text-2xl font-bold text-foreground mb-4">Recent Bookings</h2>
          {loading ? (
            <p className="text-muted-foreground text-center py-8">Loading...</p>
          ) : bookings.length === 0 ? (
            <p className="text-muted-foreground text-center py-8">No bookings yet. Start by searching for services above.</p>
          ) : (
            <div className="space-y-3">
              {bookings.slice(0, 5).map((booking) => (
                <div
                  key={booking.id}
                  className="flex items-center justify-between p-4 border border-border rounded-sm hover:shadow-sm transition-shadow"
                >
                  <div>
                    <p className="font-medium text-foreground">Booking #{booking.id.slice(0, 8)}</p>
                    <p className="text-sm text-muted-foreground">
                      {new Date(booking.booking_date).toLocaleDateString()}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="font-bold text-foreground">${(booking.total_amount || 0).toFixed(2)}</p>
                    <span className={`inline-block px-2.5 py-0.5 rounded-full text-xs font-semibold uppercase tracking-wide ${
                      booking.status === 'pending' ? 'bg-amber-100 text-amber-800 border border-amber-200' :
                      booking.status === 'active' ? 'bg-emerald-100 text-emerald-700 border border-emerald-200' :
                      'bg-slate-100 text-slate-600 border border-slate-200'
                    }`}>
                      {booking.status}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </Card>
      </div>
      <Footer />
    </div>
  );
};

export default CustomerDashboard;
